import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { BugReportClearStatus, BugReportCreateRequest, BugReportCreateSuccess } from '../../../../../store/bug-report/bug-report.actions';
import { selectBugReportSubmitting } from '../../../../../store/bug-report/bug-report.selectors';

type BugReportAttachmentItem = {
  file: File;
  previewUrl: string | null;
};

@Component({
  selector: 'app-bug-report-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
  ],
  templateUrl: './bug-report-dialog.component.html',
  styleUrl: './bug-report-dialog.component.scss',
})
export class BugReportDialogComponent {
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  private readonly formBuilder = inject(FormBuilder);
  private readonly actions$ = inject(Actions);
  private readonly dialogRef = inject(MatDialogRef<BugReportDialogComponent>);

  protected readonly submitting = signal(false);
  protected readonly attachmentItems = signal<BugReportAttachmentItem[]>([]);
  protected readonly isDropZoneActive = signal(false);

  protected readonly form = this.formBuilder.group({
    title: [''],
    description: [''],
  });

  constructor() {
    this.store.dispatch(BugReportClearStatus());

    this.store
      .select(selectBugReportSubmitting)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((submitting) => this.submitting.set(!!submitting));

    this.actions$
      .pipe(ofType(BugReportCreateSuccess), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.close(true);
      });

    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.form.controls.title.hasError('requiredEither')) {
        this.form.controls.title.setErrors(null);
      }
      if (this.form.controls.description.hasError('requiredEither')) {
        this.form.controls.description.setErrors(null);
      }
    });

    this.destroyRef.onDestroy(() => {
      this.revokeAttachmentPreviews(this.attachmentItems());
    });
  }

  protected close(submitted: boolean = false): void {
    this.store.dispatch(BugReportClearStatus());
    this.dialogRef.close(submitted);
  }

  protected onAttachmentFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    this.addFiles(Array.from(input?.files ?? []));
    if (input) {
      input.value = '';
    }
  }

  protected onAttachmentDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDropZoneActive.set(false);
    this.addFiles(Array.from(event.dataTransfer?.files ?? []));
  }

  protected onAttachmentDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDropZoneActive.set(true);
  }

  protected onAttachmentDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDropZoneActive.set(false);
  }

  protected onAttachmentPaste(event: ClipboardEvent): void {
    const files = Array.from(event.clipboardData?.files ?? []);
    if (!files.length) {
      return;
    }

    event.preventDefault();
    this.addFiles(files);
  }

  protected removeAttachment(index: number): void {
    const current = this.attachmentItems();
    const target = current[index];
    if (target?.previewUrl) {
      URL.revokeObjectURL(target.previewUrl);
    }

    this.attachmentItems.set(current.filter((_, fileIndex) => fileIndex !== index));
  }

  protected hasImagePreview(item: BugReportAttachmentItem): boolean {
    return !!item.previewUrl;
  }

  protected submit(): void {
    const title = this.form.controls.title.value?.trim() ?? '';
    const description = this.form.controls.description.value?.trim() ?? '';

    if (!title && !description) {
      this.form.controls.title.setErrors({ requiredEither: true });
      this.form.controls.description.setErrors({ requiredEither: true });
      this.form.markAllAsTouched();
      return;
    }

    const payload = new FormData();
    payload.append('title', title);
    payload.append('description', description);
    payload.append('page_url', typeof window !== 'undefined' ? window.location.href : '');
    payload.append('user_agent', typeof navigator !== 'undefined' ? navigator.userAgent : '');
    payload.append('app_version', '');

    this.attachmentItems().forEach(({ file }) => {
      payload.append('attachments', file);
    });

    this.store.dispatch(BugReportCreateRequest({ payload }));
  }

  private addFiles(files: File[]): void {
    if (!files.length) {
      return;
    }

    const existing = this.attachmentItems();
    const deduped = [...existing];
    for (const file of files) {
      const exists = deduped.some(
        (item) =>
          item.file.name === file.name
          && item.file.size === file.size
          && item.file.lastModified === file.lastModified,
      );
      if (exists) {
        continue;
      }

      deduped.push({
        file,
        previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      });
    }

    const nextItems = deduped.slice(0, 5);
    this.revokeAttachmentPreviews(deduped.slice(5));
    this.attachmentItems.set(nextItems);
  }

  private revokeAttachmentPreviews(items: BugReportAttachmentItem[]): void {
    for (const item of items) {
      if (item.previewUrl) {
        URL.revokeObjectURL(item.previewUrl);
      }
    }
  }
}
